import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { initTheme } from '@/lib/theme';
import { registerServiceWorker } from '@/pwa/useServiceWorker';
import { countVisit } from '@/pwa/useInstallPrompt';
import { App } from '@/App';
import '@/styles/index.css';

// Applied before first paint, so there is no flash of the wrong theme.
initTheme();

// Counted here rather than in a component, so a remount does not inflate it. The
// install prompt waits for the third visit.
countVisit();

/**
 * The mock API is opt-in via VITE_USE_MOCKS, never inferred from the environment: a
 * developer pointing at a real local API must not have mocks silently intercepting
 * their requests. The dynamic import keeps msw out of the production bundle entirely.
 */
async function startMocksIfEnabled(): Promise<void> {
  if (import.meta.env.VITE_USE_MOCKS !== 'true') return;
  const { startMockWorker } = await import('@/mocks/browser');
  await startMockWorker();
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

function render(): void {
  createRoot(container!).render(
    <StrictMode>
      <ErrorBoundary area="the application shell">
        <App />
        <Toaster position="bottom-right" richColors closeButton />
      </ErrorBoundary>
    </StrictMode>,
  );
}

// `finally`, not `then`: a mock worker that fails to register must not leave a blank
// page. The app still starts and talks to whatever API is configured, with the reason
// on the console.
// After the first render rather than before it: registration competes for the main
// thread, and the shell appearing matters more than the worker being ready a moment
// sooner. It is a no-op in development and under test.
void startMocksIfEnabled()
  .catch((err: unknown) => {
    // eslint-disable-next-line no-console
    console.error('[mocks] Worker did not start; continuing against the real API.', err);
  })
  .finally(() => {
    render();
    registerServiceWorker();
  });
